<template>
  <div class="projekte-page">
    <Teleport to="#site-header-slot" v-if="windowWidth">
      <ProjectOverviewToggle
        class="toggle"
        @toggle="toggleView"
        :current-view="view"
        v-show="windowWidth >= 800"
      />
    </Teleport>
    <main class="main">
      <div class="inner" :class="[`-${view}`]">
        <ProjectOverviewToggle
          class="toggle"
          @toggle="toggleView"
          :current-view="view"
          v-if="windowWidth <= 799"
        />
        <ul
          ref="projectFilterElement"
          class="filters project-filter"
          :class="{ '-open': isFilersPanelOpen }"
        >
          <button
            @click="toggleFiltersPanel()"
            class="toggle button-toggle"
            :class="{ '-open': isFilersPanelOpen }"
          >
            {{ general?.projectOpenCloseFilter }}
          </button>
          <li class="filter title" @click="toggleFiltersPanel()">Filter</li>
          <li class="filter">
            <button
              :class="{ '-active': activeFilter === null }"
              @click="setFilter(null)"
              class="button button-simple"
            >
              {{ general?.projectFilterAllServices }}
            </button>
          </li>
          <li
            @click="setFilter(s.slug ?? null)"
            class="filter"
            v-for="s in allServices"
            :key="s.id"
            :data-service="s.slug"
          >
            <button class="button button-simple" :class="{ '-active': activeFilter === s.slug }">
              {{ s.title }}
            </button>
          </li>
        </ul>
        <div class="projectfocus">
          <ProjectTeaserImage
            :key="activeProject.id"
            :data-old="oldActiveProject ?? undefined"
            :data="activeProject"
            v-if="activeProject"
          />
        </div>
        <div class="projects" v-if="view === 'raster'">
          <ProjectTeaser :project="p" v-for="p in filteredProjects" :key="p.id" />
        </div>
        <div class="projects" v-else-if="view === 'list'">
          <ProjectTeaserTitle
            :data="p"
            v-for="p in filteredProjects"
            :key="p.id"
            @mouseenter="setActiveProject(p)"
            :class="{ '-active': activeProject === p }"
          />
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap';
import ProjectTeaser from '~/components/content-modules/components/ProjectTeaser.vue';
import ProjectTeaserTitle from '~/components/content-modules/components/ProjectTeaserTitle.vue';
import ProjectTeaserImage from '~/components/content-modules/components/ProjectTeaserImage.vue';
import ProjectOverviewToggle from '~/components/content-modules/components/ProjectOverviewToggle.vue';
import type { GetAllProjectsQuery } from '#gql';
import type { GetAllServicesQuery } from '#gql';
import type { ProjectFragment } from '#gql';
import { type SeoMetaTagType, toHead } from 'vue-datocms';
import type { GetPageBySlugQuery } from '#gql';
import type { GeneralFragment } from '#gql';
import type { ComputedRef } from 'vue';

enum ProjectView {
  List = 'list',
  Raster = 'raster'
}

const general = inject<ComputedRef<GeneralFragment | null>>('general');

const route = useRoute();

const { data: pageData, error: pageError } = await useAsyncGql({
  operation: 'GetPageBySlug',
  variables: { slug: String(route.name) }
});

const { data: projectsData, error: projectsError } = await useAsyncGql({
  operation: 'GetAllProjects'
});

const { data: servicesData, error: servicesError } = await useAsyncGql({
  operation: 'GetAllServices'
});

const page = (pageData.value as GetPageBySlugQuery).page;
const allProjects = (projectsData.value as GetAllProjectsQuery).allProjects;
const allServices = (servicesData.value as GetAllServicesQuery).allServices;

const projectFilterElement = ref<HTMLElement | null>(null);
const isFilersPanelOpen = ref<boolean>(false);
const activeFilter = ref<null | string>(null);
const oldActiveProject = ref<null | ProjectFragment>(null);
const activeProject = ref<null | ProjectFragment>(null);
const view = ref<ProjectView>(ProjectView.Raster);
const { width: windowWidth } = useWindowSize();

onClickOutside(projectFilterElement, function () {
  toggleFiltersPanel(false);
});

const filteredProjects = computed(function () {
  if (activeFilter.value) {
    return allProjects.filter((s) => s.service?.slug === activeFilter.value);
  }

  return allProjects;
});

watch(
  () => filteredProjects.value.length,
  function () {
    if (filteredProjects.value[0]) {
      setActiveProject(filteredProjects.value[0]);
    }
  }
);

setActiveProject(filteredProjects.value?.[0] ?? null);

function setActiveProject(p: null | ProjectFragment) {
  oldActiveProject.value = activeProject.value;
  activeProject.value = p;
}

function setFilter(f: null | string) {
  activeFilter.value = f;

  setActiveProject(null);
}

function toggleView(v: ProjectView) {
  view.value = v;
}

function toggleFiltersPanel(open = !isFilersPanelOpen.value) {
  isFilersPanelOpen.value = open;
}

useHead(toHead(page?.seo as SeoMetaTagType[]));

definePageMeta({
  pageTransition: {
    name: 'from-index',
    mode: 'out-in',
    onLeave: async function (el, done) {
      await useSleep(100);

      const tl = gsap.timeline({
        onComplete: function () {
          done();
        }
      });

      tl.to(el, {
        opacity: 0
      });
    }
  }
});
</script>

<style scoped lang="scss">
.projekte-page {
  > .main {
    > .inner {
      display: grid;

      @media (--vs) {
        grid-template-columns: 1fr;
        gap: 2rem;
        padding: var(--base-component-padding-block) var(--base-component-padding-inline) 5rem
          var(--base-component-padding-inline);
      }

      @media (--vl) {
        grid-template-columns: 1fr 2fr;
        grid-template-rows: auto 1fr;
        gap: 2.5rem;
        padding: 5rem var(--base-component-padding-inline) 0 var(--base-component-padding-inline);
      }

      > .toggle {
        @media (--vl) {
          display: none;
        }
      }

      &.-raster {
        padding-bottom: 5rem;

        > .filters {
          @media (--vl) {
            padding-top: 3.5rem;
            grid-area: 1 / 1 / 2 / 2;
          }
        }

        > .projectfocus {
          display: none;
        }

        > .projects {
          display: grid;

          @media (--vs) {
            gap: 1.5rem;
            grid-template-columns: repeat(1, minmax(0, 1fr));
            padding: 0 0 2rem 0;

            :deep(img),
            :deep(figure) {
              --scale: 1;
              scale: 1 !important;
            }
          }

          @media (--vl) {
            gap: 2.5rem 2rem;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            padding-top: 6rem;
            grid-area: 2 / 1 / 3 / 3;
          }
        }
      }

      &.-list {
        @media (--vs) {
          display: block;
        }

        @media (--vl) {
          height: 100vh;
          gap: 3rem 15vw;
        }

        > .toggle {
        }

        > .filters {
          @media (--vl) {
            grid-area: 1 / 1 / 2 / 2;
            height: fit-content;
            padding-top: 3.5rem;
          }
        }

        > .projectfocus {
          @media (--vs) {
            display: none;
          }
          @media (--vl) {
            grid-area: 2 / 1 / 3 / 2;
            padding-bottom: var(--base-component-padding-inline);
            align-self: flex-end;
          }
        }

        > .projects {
          @include hide-scrollbar();

          @media (--vs) {
            padding-bottom: 2rem;
          }

          @media (--vl) {
            grid-area: 1 / 2 / 3 / 3;
            padding-top: 3.5rem;
            padding-bottom: 3.5rem;
            overflow-y: auto;
          }
        }
      }
    }
  }
}

.project-filter {
  @media (--vs) {
    position: fixed;
    inset: auto 0 0 0;
    width: 100%;
    z-index: 10;
    padding: 1.5rem;
    background-color: var(--color-black);
    transition: transform 300ms;
    transform: translateY(calc(100% - 4.5rem));

    > .filter,
    > .toggle {
      transition: opacity 300ms;
    }

    > .toggle {
      filter: invert(1);
    }

    > .filter:not(:first-of-type) {
      opacity: 0;
    }

    > .filter:not(:last-of-type) {
      padding: 0 0 1.25rem 0;
    }

    > .filter {
      > .button.-active {
        color: var(--color-white);
      }

      > .button:hover {
        color: var(--color-white);
      }
    }

    &.-open {
      transform: translateY(0);

      > .filter:not(:first-of-type) {
        opacity: 1;
      }
    }
  }

  > .toggle {
    @media (--vl) {
      display: none;
    }

    @media (--vs) {
      top: 1.5rem;
      right: 1.5rem;
      z-index: 10;
    }
  }

  > .filter {
    > .button {
      @media (--vs) {
        @include text-xl(700);

        line-height: 1 !important;
        width: 100%;
      }
    }
  }

  > .title {
    @include text-xl(700);

    color: var(--color-white);
    line-height: 1 !important;

    @media (--vl) {
      display: none;
    }
  }
}
</style>
